import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { IMAGES } from "@/lib/data";
import { useLang } from "@/lib/i18n";
import { useSeo, useHreflang } from "@/components/seo/Seo";
import { LANGS, STATIC_COPY, S, p } from "@/lib/seoContent";

const ease = [0.22, 1, 0.36, 1];
const MOMENT_IMAGES = [IMAGES.chauffeurDoor, IMAGES.chauffeurSuit, IMAGES.traveler];

const FadeIn = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Chauffeurs() {
  const { t, lang } = useLang();
  const seoPage = STATIC_COPY.chauffeurs[lang] || STATIC_COPY.chauffeurs.fr;
  useSeo({ lang, title: seoPage.title, description: seoPage.meta, path: p(lang, "chauffeurs"), type: "chauffeurs", id: "" });
  useHreflang(Object.fromEntries(LANGS.map((l) => [l, p(l, "chauffeurs")])));
  return (
    <div className="min-h-screen bg-white text-ink" data-testid="chauffeurs-page">
      <Navbar />

      <section className="bg-gradient-to-b from-miles-ice/60 to-white pb-16 pt-32 lg:pb-20 lg:pt-40" data-testid="chauffeurs-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn y={16}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{t("dr.eyebrow")}</p>
            <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] sm:text-5xl lg:text-6xl">
              {t("dr.h1a")}
              <br />
              <span className="font-light text-slate/55">{t("dr.h1b")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate sm:text-lg">{t("dr.sub")}</p>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-ink/10 pt-6" data-testid="chauffeurs-trust">
              {t("dr.trust").map((item) => (
                <p key={item} className="flex items-center gap-2.5 text-sm font-medium text-ink/80">
                  <Check className="h-4 w-4 shrink-0 text-miles" strokeWidth={2} /> {item}
                </p>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="chauffeurs-moments">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("dr.moments_eyebrow")}</p>
            <h2 className="mt-5 text-3xl font-semibold leading-[1.02] tracking-[-0.045em] text-ink sm:text-4xl lg:text-5xl">
              {t("dr.moments_h2a")}{" "}
              <span className="font-light text-slate/55">{t("dr.moments_h2b")}</span>
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-4 sm:grid-cols-3 lg:gap-6">
            {t("dr.moments").map(([title, desc], i) => (
              <FadeIn key={title} delay={i * 0.1}>
                <div className="group relative overflow-hidden rounded-[2rem]" data-testid={`chauffeur-moment-${i}`}>
                  <img src={MOMENT_IMAGES[i]} alt={title} loading="lazy"
                    className="img-clean aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-miles-deep/85 via-miles-deep/15 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-miles-sky">{String(i + 1).padStart(2, "0")}</p>
                    <p className="mt-2 text-xl font-semibold tracking-tight text-white">{title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/75">{desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-miles-deep py-24 lg:py-32" data-testid="chauffeurs-steps-section">
        <div className="pointer-events-none absolute -left-32 top-0 h-80 w-80 rounded-full bg-miles/20 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles-sky">{t("dr.steps_eyebrow")}</p>
            <h2 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-white sm:text-5xl">
              {t("dr.steps_h2a")}
              <br />
              <span className="font-light text-white/60">{t("dr.steps_h2b")}</span>
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70">{t("dr.steps_sub")}</p>
          </FadeIn>
          <div className="mt-14 grid gap-px overflow-hidden rounded-[2rem] border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
            {t("dr.steps").map(([title, desc], i) => (
              <FadeIn key={title} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col bg-miles-deep p-7" data-testid={`chauffeur-step-${i}`}>
                  <p className="font-mono text-[11px] font-semibold tracking-[0.25em] text-miles-sky">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 text-lg font-semibold tracking-tight text-white">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cloud py-24 lg:py-32" data-testid="chauffeurs-commitments">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("dr.eyebrow")}</p>
            <h2 className="mt-5 max-w-2xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-ink sm:text-5xl">
              {t("dr.h2a")}{" "}
              <span className="font-light text-slate/55">{t("dr.h2b")}</span>
            </h2>
          </FadeIn>
          <div className="mt-12 border-t border-ink/10">
            {t("dr.commitments").map(([title, desc], i) => (
              <FadeIn key={title} delay={i * 0.05} y={16}>
                <div data-testid={`chauffeur-commitment-${i}`}
                  className="group grid gap-2 border-b border-ink/10 py-8 sm:grid-cols-[64px_1fr_1.1fr] sm:items-baseline sm:gap-10">
                  <span className="font-mono text-xs font-semibold tracking-[0.2em] text-miles">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-xl font-semibold tracking-tight text-ink transition-transform duration-300 group-hover:translate-x-1.5 sm:text-2xl">{title}</p>
                  <p className="text-sm leading-relaxed text-slate">{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-miles-deep py-24 lg:py-32" data-testid="chauffeurs-final-section">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-miles/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 left-1/4 h-80 w-80 rounded-full bg-miles-electric/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles-sky">{t("dr.band_eyebrow")}</p>
            <h2 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-white sm:text-5xl">
              {t("dr.band_t")}
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">{t("dr.band_s")}</p>
            <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
              <Link to="/contact" data-testid="become-driver-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-miles-deep transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-ice">
                {t("dr.band_cta")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Link to="/flotte" data-testid="chauffeurs-fleet-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-white/85 transition-colors hover:text-white">
                {t("dr.band_alt")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
